import type { GetToken } from "./canvasService";
import { apiUrl } from "@/config/api";
import { tracedFetch } from "@/utils/tracedFetch";

export interface AdminStatus {
  lockdown: boolean;
}

export interface AdminMcpUser {
  userId: string;
  orgId: string;
  lastUsed: string | null;
  blocked: boolean;
}

interface WireMcpUser {
  user_id: string;
  org_id: string;
  last_used?: string | null;
  blocked: boolean;
}

async function authedFetch(url: string, getToken: GetToken, init?: RequestInit): Promise<Response> {
  const token = await getToken();
  return tracedFetch(apiUrl(url), {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

async function orThrow(res: Response): Promise<Response> {
  if (!res.ok) {
    const body = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${body}`);
  }
  return res;
}

/**
 * Null for everyone who is not a platform admin — the backend answers 404
 * there (never 403), so a null is the expected shape of "not you", not an
 * error to surface.
 */
export async function getAdminStatus(getToken: GetToken): Promise<AdminStatus | null> {
  const res = await authedFetch("/api/admin/status", getToken);
  if (res.status === 404) return null;
  await orThrow(res);
  return (await res.json()) as AdminStatus;
}

export async function setMcpLockdown(getToken: GetToken, active: boolean): Promise<void> {
  await orThrow(
    await authedFetch("/api/admin/mcp-lockdown", getToken, {
      method: "PUT",
      body: JSON.stringify({ active }),
    }),
  );
}

export async function listMcpUsers(getToken: GetToken): Promise<AdminMcpUser[]> {
  const res = await orThrow(await authedFetch("/api/admin/mcp-users", getToken));
  const wire = (await res.json()) as { users: WireMcpUser[] };
  return wire.users.map((u) => ({
    userId: u.user_id,
    orgId: u.org_id,
    lastUsed: u.last_used ?? null,
    blocked: u.blocked,
  }));
}

export async function setMcpUserBlock(
  getToken: GetToken,
  userId: string,
  blocked: boolean,
): Promise<void> {
  await orThrow(
    await authedFetch(`/api/admin/mcp-users/${encodeURIComponent(userId)}/block`, getToken, {
      method: "PUT",
      body: JSON.stringify({ blocked }),
    }),
  );
}
